import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
} from 'react'
import '../../stylesheets/ACQ7.scss'
import SurveyQuestion from '../SurveyQuestion'
import QuestionFooter from '../QuestionFooter'
import { formatAndSanitizeHTML } from 'utils/misc'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'
import MakeScrollable from '../MakeScrollable'
import { ACQ7_INPUT_LABELS } from 'utils/constants'

const fevScores = [
  { score: 0, label: '>95% predicted' },
  { score: 1, label: '95 - 90%' },
  { score: 2, label: '89 - 80%' },
  { score: 3, label: '79 - 70%' },
  { score: 4, label: '69 - 60%' },
  { score: 5, label: '59 - 50%' },
  { score: 6, label: '<50% predicted' },
]

const InputField = ({
  label,
  value,
  onChange,
  id,
  error,
  disabled,
  maxLength,
}) => {
  return (
    <div className={`input-group ${error ? 'has-error' : ''}`}>
      <label htmlFor={id}>{label}</label>
      <div className='input-error-wrapper'>
        <input
          type='number'
          step='0.1'
          id={id}
          value={value}
          onChange={onChange}
          disabled={disabled}
          maxLength={maxLength}
        />
        {error && <div className='error-message'>{error}</div>}
      </div>
    </div>
  )
}

const ScoreOption = ({ item, selected }) => (
  <div
    className={`score-option ${selected ? 'selected' : ''}`}
    tabIndex={0}
    role='button'
    aria-pressed={selected}>
    <span>{item.score}</span> = {item.label}
  </div>
)

const ACQ7View = forwardRef((props, ref) => {
  const {
    webSurvey,
    id,
    submissionSurvey,
    nextQId,
    nextQ,
    completeQuestion,
    strings,
    storedAnswer,
    question,
    renderBackButton,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
    submissionLoading,
  } = props

  const { label, type, attributes } = question
  const { required } = attributes || {}

  const [fevPre, setFevPre] = useState('')
  const [fevPredicted1, setFevPredicted1] = useState('')
  const [fevPredicted2, setFevPredicted2] = useState('')
  const [selectedScore, setSelectedScore] = useState(null)

  const [errors, setErrors] = useState({
    fevPre: '',
    fevPredicted1: '',
    fevPredicted2: '',
  })

  const [hasPrefilled, setHasPrefilled] = useState(false)
  const [hasCheckedStoredAnswer, setHasCheckedStoredAnswer] = useState(false)

  const isFilled = value => value !== null && value !== ''

  const isComplete = useMemo(
    () =>
      selectedScore !== null &&
      isFilled(fevPre) &&
      isFilled(fevPredicted1) &&
      isFilled(fevPredicted2),
    [selectedScore, fevPre, fevPredicted1, fevPredicted2],
  )

  const hasErrors = useMemo(() => {
    return Object.values(errors).some(error => error !== '')
  }, [errors])

  const createAnswerObjects = useCallback(
    () => [
      {
        question: id,
        label: ACQ7_INPUT_LABELS.fevPreBronchodilator,
        value: fevPre,
      },
      {
        question: id,
        label: ACQ7_INPUT_LABELS.fevPredicted1,
        value: fevPredicted1,
      },
      {
        question: id,
        label: ACQ7_INPUT_LABELS.fevPredicted2,
        value: fevPredicted2,
      },
      { question: id, label: ACQ7_INPUT_LABELS.fevScore, value: selectedScore },
    ],
    [id, fevPre, fevPredicted1, fevPredicted2, selectedScore],
  )

  const calculateFEVPredicted2 = useCallback(() => {
    const fevPreNum = parseFloat(fevPre)
    const fevPredicted1Num = parseFloat(fevPredicted1)

    if (
      !fevPreNum ||
      !fevPredicted1Num ||
      isNaN(fevPreNum) ||
      isNaN(fevPredicted1Num)
    ) {
      setFevPredicted2('0')
      setSelectedScore(null)
      return
    }

    const percentage = (fevPredicted1Num / fevPreNum) * 100
    const percentageRounded = Math.round(parseFloat(percentage.toFixed(2)))
    setFevPredicted2(percentageRounded.toString())

    // set or clean fevPredicted2 errors
    setErrors(prev => ({
      ...prev,
      fevPredicted2:
        percentageRounded < 0 || percentageRounded > 100
          ? 'Percentage value must be between 0 and 100'
          : '',
    }))

    const matchingScore = fevScores.find(({ label }) => {
      if (label.includes('>')) {
        const min = parseFloat(label.match(/>(\d+)/)?.[1])
        return percentageRounded > min
      }
      if (label.includes('<')) {
        const max = parseFloat(label.match(/<(\d+)/)?.[1])
        return percentageRounded < max
      }

      const [min, max] = label.match(/\d+/g)?.map(Number) || []
      return percentageRounded <= min && percentageRounded >= max
    })

    if (matchingScore) {
      setSelectedScore(matchingScore.score.toString())
    } else {
      setSelectedScore(null)
    }
  }, [fevPredicted1, fevPre])

  useEffect(() => {
  if (fevPre !== '' && fevPredicted1 !== '') {
    calculateFEVPredicted2()
  }
}, [fevPre, fevPredicted1, calculateFEVPredicted2])

  useEffect(() => {
    if (!hasPrefilled && storedAnswer && storedAnswer.length) {
      storedAnswer.forEach(({ value, label }) => {
        if (label === ACQ7_INPUT_LABELS.fevPreBronchodilator) setFevPre(value)
        else if (label === ACQ7_INPUT_LABELS.fevPredicted1)
          setFevPredicted1(value)
        else if (label === ACQ7_INPUT_LABELS.fevPredicted2)
          setFevPredicted2(value)
        else if (label === ACQ7_INPUT_LABELS.fevScore)
          setSelectedScore(value.toString())
      })
      setHasPrefilled(true)
    }
    setHasCheckedStoredAnswer(true)
  }, [storedAnswer, hasPrefilled])

  useEffect(() => {
    if (!hasCheckedStoredAnswer) return

    const isInitiallyComplete =
      selectedScore !== null &&
      fevPre !== '' &&
      fevPredicted1 !== '' &&
      fevPredicted2 !== ''

    if (isInitiallyComplete) {
      onCompleteACQ7Question()
    }
  }, [hasCheckedStoredAnswer])

  const validateField = value => {
    const num = parseFloat(value)

    if (value === '') {
      return 'This field is required.'
    }

    if (isNaN(num) || num < 0 || num > 10.0) {
      return 'Value must be between 0.0 and 10.0'
    }

    return ''
  }

  const handleInputChange = (name, value, setter, maxLength = 6) => {
    if (value.length < maxLength) {
      const errorMsg = validateField(value)
      setErrors(prev => ({ ...prev, [name]: errorMsg }))
      // Allow empty string for typing
      setter(String(value))
    }
  }

  const onCompleteACQ7Question = () => {
    const answer = createAnswerObjects()
    const submission = {
      id,
      label,
      name: label,
      type,
      required,
      state: isComplete ? 'ANSWERED' : 'SKIPPED',
      answer,
    }

    const currentAnswer = { [id]: answer }

    const shouldRenderNextQ = getShouldRenderNextQuestion({
      isConsent,
      isLastQuestion,
      isSibling,
    })

    checkLogicAndComplete(
      webSurvey,
      id,
      submissionSurvey,
      currentAnswer,
      nextQId,
      nextQ,
      completeQuestion,
      submission,
      reviewFunctionalitySupported,
      shouldRenderNextQ,
    )
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div className='survey-question' ref={ref}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label),
              }}
            />
          </p>
          <div className='fev-form'>
            <h3>
              {strings.to_be_completed_by_member_of_clinic_staff ||
                'To be completed by a member of the clinic staff'}
            </h3>
            <p className='subtitle'>
              {strings.record_actual_values || 'Record actual values'}
            </p>

            <InputField
              label={strings.fev_pre_bronchodilator || 'FEV₁pre-bronchodilator'}
              value={fevPre}
              onChange={e => {
                handleInputChange('fevPre', e.target.value, setFevPre)
              }}
              id='fev-pre'
              error={errors.fevPre}
              maxLength={5}
            />
            <InputField
              label={strings.fev_predicted || 'FEV₁predicted'}
              value={fevPredicted1}
              onChange={e => {
                handleInputChange(
                  'fevPredicted1',
                  e.target.value,
                  setFevPredicted1,
                )
              }}
              id='fev-predicted-1'
              error={errors.fevPredicted1}
              maxLength={5}
            />
            <InputField
              label={strings.fev_percent_predicted || 'FEV₁%predicted'}
              value={fevPredicted2}
              id='fev-predicted-2'
              disabled={true}
              error={errors.fevPredicted2}
            />

            <p>
              {strings.score_the_fev_percent_predicted ||
                'and score the FEV₁% predicted below'}
            </p>
            <div className='score-options'>
              {fevScores.map(item => (
                <ScoreOption
                  key={item.score}
                  item={item}
                  selected={selectedScore === item.score.toString()}
                />
              ))}
            </div>

            <QuestionFooter
              completed={isComplete}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={onCompleteACQ7Question}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          </div>
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
})

export default ACQ7View
